/**
 * @fileoverview A packet's (or a kind's) picture as its plant will keep it: its own part of the photo cut out
 * (PacketCut), or the whole photo's thumbnail when it has no position (PacketThumb). A picture only, with no icon and
 * no tap to swap it (B59). Moved out of PacketReview for D78 (2026-10-22), unchanged, so the rows and their change
 * dialog (usePacketRows) can draw it without importing the review sheet.
 *
 * Where: the packet and plant rows (PacketRow, through usePacketRows and KindsPile), their change dialog; PacketReview
 * re-exports both for older importers.
 * API: none.
 * Settings: THUMB (the thumbnail's side, kept here); the server's limits.PACKET_CROP_MARGIN (lib/growSettings).
 *
 * @module PacketPicture
 */

import React from 'react';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';
import { cn } from '@/lib/utils';
import { useGrowLimits } from '@/shared/lib/growSettings';

/**
 * A packet's thumbnail: a square, the size of a thumb, in px; used to scale and centre the cut-out packet inside it
 * (the review sheet's thumbnail side; moved here from the value file, C118 F10).
 * @kept K4
 */
const THUMB = 56;

/**
 * One packet (or one kind of a photo of several, KindsPile) cut out of the photo, as its plant will keep it, in a
 * square: the box (with the margin the server cuts with) filling the square, its middle in the middle, as a cover
 * crop would.
 *
 * @param props.src - The photo.
 * @param props.box - [ymin, xmin, ymax, xmax] on 0-1000.
 */
export const PacketCut: React.FC<{ src: string; box: [number, number, number, number] }> = ({ src, box }) => {
  const [size, setSize] = React.useState<{ w: number; h: number } | null>(null);
  const margin = useGrowLimits().PACKET_CROP_MARGIN;
  // The margin the server cuts with (limits.PACKET_CROP_MARGIN, photoBox); the cut is hidden until it is known.
  const [y0, x0, y1, x1] = box.map((n) => n / 1000);
  const m = margin ?? 0;
  const left = Math.max(0, x0 - m);
  const top = Math.max(0, y0 - m);
  const width = Math.min(1, x1 + m) - left;
  const height = Math.min(1, y1 + m) - top;
  // Scaled so the cut covers the square, then centred on it.
  const style: React.CSSProperties = size && margin !== undefined ? (() => {
    const scale = Math.max(THUMB / (width * size.w), THUMB / (height * size.h));
    return {
      width: size.w * scale,
      left: -left * size.w * scale + (THUMB - width * size.w * scale) / 2,
      top: -top * size.h * scale + (THUMB - height * size.h * scale) / 2,
    };
  })() : { opacity: 0 };
  return (
    <span className="relative block overflow-hidden rounded-sm bg-muted" style={{ width: THUMB, height: THUMB }}>
      <img
        src={src}
        alt=""
        onLoad={(event) => setSize({ w: event.currentTarget.naturalWidth, h: event.currentTarget.naturalHeight })}
        className="absolute max-w-none"
        style={style}
      />
    </span>
  );
};

/**
 * A packet photo's thumbnail in its square: filling it, or, if the photo is smaller than the square, at its own size
 * on a clear background (B32).
 *
 * @param props.src - The thumbnail's URL.
 * @param props.className - Extra classes for the square (such as shrink-0).
 */
export const PacketThumb: React.FC<{ src: string; className?: string }> = ({ src, className }) => (
  <span className={cn('relative block overflow-hidden rounded-sm bg-muted data-[small=true]:bg-transparent', className)} style={{ width: THUMB, height: THUMB }}>
    <FramedPhoto src={src} alt="" />
  </span>
);
